import Terraformer from 'terraformer-arcgis-parser';
import { MapDrawFuncParams } from 'types';
import { getFeatureServerUrl } from 'services/map.service';
import MapSettings from 'components/Map/settings';
import { addLayerToMap, findLayerById } from 'utilities/map-instance.util';

const { previewAOIDarkSymbol, previewAOIlightSymbol } = MapSettings;

const transparentSymbol = {
  type: 'simple-fill',
  style: 'none',
  outline: {
    style: 'none'
  }
};

const convertLineToPolygon = (
  graphic: __esri.Graphic,
  drawingLayer: __esri.GraphicsLayer,
  symbol: any
) => {
  const { loadedMapModules } = window;
  const { GeometryEngine, WebMercatorUtils, Graphic } = loadedMapModules;
  const buffer = GeometryEngine.geodesicBuffer(graphic.geometry, 10, 'meters');
  const newGraphic = new Graphic({
    geometry: buffer,
    symbol
  });
  drawingLayer.remove(graphic);
  drawingLayer.add(newGraphic);

  return WebMercatorUtils.webMercatorToGeographic(buffer);
};

export const addGraphicFromGeoJson = (
  geoJson: GeoJSON.GeometryObject,
  symbol: any
) => {
  const { loadedMapModules } = window;
  const { Graphic, WebMercatorUtils } = loadedMapModules;
  // Use centralized utility to find layer
  const drawingLayer: any = findLayerById('drawing-layer');
  const geometry: any = Terraformer.convert(geoJson);
  const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
    geometry
  );
  const fullGraphic = new Graphic({
    geometry: {
      ...webMercatorGeometry,
      type: 'polygon'
    },
    symbol
  });

  if (drawingLayer) {
    drawingLayer.removeAll();
    if (
      Array.isArray(webMercatorGeometry.rings) &&
      webMercatorGeometry.rings.length > 1
    ) {
      const newGraphics: any[] = [];
      webMercatorGeometry.rings.forEach((ring: any) => {
        const graphic = new Graphic({
          geometry: {
            ...webMercatorGeometry,
            rings: [ring],
            type: 'polygon'
          },
          symbol
        });
        newGraphics.push(graphic);
      });
      drawingLayer.addMany(newGraphics);
    } else {
      drawingLayer.add(fullGraphic);
    }
    drawingLayer.emit('change');

    zoomToExtent(fullGraphic.geometry.extent);
  }
};

export const getGeoJsonFromGraphics = (
  { drawingLayer }: MapDrawFuncParams,
  symbol: any
): any => {
  const { loadedMapModules } = window;
  const { WebMercatorUtils } = loadedMapModules;
  const graphics = drawingLayer.graphics.toArray();

  if (graphics.length > 0) {
    if (graphics.length === 1) {
      const graphic: any = graphics[0];
      graphic.symbol = symbol;
      const geoGraphicGeometry =
        graphic.geometry.type === 'polyline'
          ? convertLineToPolygon(graphic, drawingLayer, symbol)
          : WebMercatorUtils.webMercatorToGeographic(graphic.geometry);
      return Terraformer.parse(geoGraphicGeometry);
    } else {
      const coordinates: any[] = [];
      graphics.forEach((graphic: any) => {
        graphic.symbol = symbol;
        const geoGraphicGeometry =
          graphic.geometry.type === 'polyline'
            ? convertLineToPolygon(graphic, drawingLayer, symbol)
            : WebMercatorUtils.webMercatorToGeographic(graphic.geometry);
        const geoJson: any = Terraformer.parse(geoGraphicGeometry);
        Array.isArray(geoJson.coordinates) &&
          coordinates.push(geoJson.coordinates);
      });

      return {
        type: 'MultiPolygon',
        coordinates
      };
    }
  }

  return null;
};

export const addAoiToDrawingLayer = (
  features: __esri.Graphic[] | string | undefined,
  symbol: any
) => {
  const { loadedMapModules } = window;
  const { Graphic, WebMercatorUtils } = loadedMapModules;
  // Use centralized utility to find layer
  const drawingLayer: any = findLayerById('drawing-layer');
  if (Array.isArray(features) && drawingLayer) {
    // Clear existing graphics to prevent duplicates
    drawingLayer.removeAll();

    const newGraphics: any[] = [];
    features.forEach((graphic: __esri.Graphic) => {
      const geometry: any = graphic.geometry;
      const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
        geometry
      );
      webMercatorGeometry.rings.forEach((ring: any) => {
        const newGraphic = new Graphic({
          geometry: {
            spatialReference: webMercatorGeometry.spatialReference,
            rings: [ring],
            type: 'polygon'
          },
          symbol
        });
        newGraphics.push(newGraphic);
      });
    });
    drawingLayer.addMany(newGraphics);
  }
};

export const clearGraphics = () => {
  // Use centralized utility to find layer
  const drawingLayer: any = findLayerById('drawing-layer');
  if (drawingLayer) {
    drawingLayer.removeAll();
  }
};

export const zoomToExtent = (extent: any) => {
  const { loadedView, loadedMapModules } = window;
  if (loadedMapModules && loadedView && !loadedView.destroyed) {
    const { GeometryEngine } = loadedMapModules;
    const bufferExtent = GeometryEngine.geodesicBuffer(extent, 1, 'kilometers');
    loadedView.goTo(bufferExtent.extent).catch((error: any) => {
      if (error.name !== 'AbortError') {
        console.warn('Error zooming to extent:', error);
      }
    });
  }
};

export const enableClickOnMap = (bool: boolean) => {
  const { loadedSketchViewModel } = window;
  if (loadedSketchViewModel) {
    loadedSketchViewModel.updateOnGraphicClick = bool;
  }
};

export const setPreviewLayerTransparent = (
  transparent: boolean,
  symbol: any
) => {
  // Use centralized utility to find layer
  const layer: any = findLayerById('preview-aoi-layer');
  if (layer) {
    layer.renderer = {
      type: 'simple',
      symbol: transparent ? transparentSymbol : symbol
    };
  }
};

export const setDrawingLayerTransparent = (
  transparent: boolean,
  symbol: any
) => {
  // Use centralized utility to find layer
  const drawingLayer: any = findLayerById('drawing-layer');
  if (drawingLayer) {
    drawingLayer.graphics.items.forEach(function (graphic: any) {
      graphic.symbol = transparent ? transparentSymbol : symbol;
    });
  }
};

export const addOrRefreshSelectedAoiFeatureLayer = (
  selectedId: string,
  dark: boolean = false
) => {
  const { loadedMapModules } = window;
  if (loadedMapModules) {
    const { FeatureLayer } = loadedMapModules;
    // Use centralized utility to find layer
    const selectedAoiFeatureLayer: any = findLayerById('selected-aoi-layer');
    if (selectedAoiFeatureLayer) {
      selectedAoiFeatureLayer.definitionExpression = `group_id = ${selectedId}`;
      selectedAoiFeatureLayer.refresh();
      return selectedAoiFeatureLayer;
    } else {
      const featureLayer = new FeatureLayer({
        id: 'selected-aoi-layer',
        url: getFeatureServerUrl('AreasOfInterest'),
        outFields: ['objectid'],
        definitionExpression: `group_id = ${selectedId}`,
        renderer: {
          type: 'simple',
          symbol: dark ? previewAOIDarkSymbol : previewAOIlightSymbol
        }
      });

      // Use centralized utility to add layer
      addLayerToMap(featureLayer);

      return featureLayer;
    }
  }
};

export const removeSelectedAoiFeatureLayer = () => {
  // Use centralized utility to find and remove layer
  const layer: any = findLayerById('selected-aoi-layer');
  if (layer) {
    const loadedView = window.loadedView;
    if (loadedView) {
      loadedView.map.remove(layer);
    }
  }
};

export const zoomToTenantExtent = (tenantBbox: any) => {
  const { loadedMapModules } = window;
  if (loadedMapModules) {
    const { Polygon, WebMercatorUtils } = loadedMapModules;
    const geometry = Terraformer.convert(tenantBbox);
    const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
      geometry
    );
    const polygon = new Polygon(webMercatorGeometry);
    zoomToExtent(polygon);
  }
};
